/* eslint-disable max-len */
/* eslint-disable no-console */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    addEdge,
    applyEdgeChanges,
    applyNodeChanges,
    Connection,
    Edge,
    EdgeChange,
    NodeChange,
    Node as RcFlowNode,
} from 'reactflow';
import { connectionLineConfig } from '../../../components/FlowEditor/constants';

export type NodeData = {
  referenceId?: string;
  isConnectionActive?: boolean;
  messageContent?: string;
  keywordsContent?: string;
  trigger_keywords?: string[];
  matching_config?: {
    strategy: string;
    lexical_threshold?: number;
    case_sensitive: boolean;
    normalize_text: boolean;
  };
  triggerCondition?: string;
  similarityThreshold?: number;
  ignoreCase?: boolean;
  normalizeText?: boolean;
  filterContent?: string;
  toolsContent?: string;
  isInitial?: boolean;
  intentKeywords?: string | null;
  filters: string[] | null;
  message: string;
  tool: string;
  tools: string[];
  actions?: any;
  attachments?: Array<{
    url: string;
    name: string;
  }>;
};

export type UpdateNodePayload = {
  nodeId: string;
  newData: Partial<NodeData>;
};

interface RFState {
  nodes: RcFlowNode<NodeData>[];
  edges: Edge[];
}

const initialState: RFState = {
  nodes: [],
  edges: [],
};

const flowSlice = createSlice({
  name: 'flow',
  initialState,
  reducers: {

    setNodes(state, action: PayloadAction<RcFlowNode<NodeData>[]>) {
      state.nodes = action.payload;
    },

    setEdges(state, action: PayloadAction<Edge[]>) {
      state.edges = action.payload;
    },

    setNode(state, action: PayloadAction<RcFlowNode>) {
      state.nodes = [...state.nodes, action.payload];
    },

    onNodesChange(state, action: PayloadAction<NodeChange[]>) {
      state.nodes = applyNodeChanges(action.payload, state.nodes);
    },

    onEdgesChange(state, action: PayloadAction<EdgeChange[]>) {
      state.edges = applyEdgeChanges(action.payload, state.edges);
    },

    onConnect(state, action: PayloadAction<Connection>) {
      state.edges = addEdge(
        {
          ...action.payload,
          ...connectionLineConfig,
        },
        state.edges,
      );
    },

    onRemoveNode(state, action: PayloadAction<string>) {
      state.nodes = state.nodes.filter((node) => node.id !== action.payload);
      state.edges = state.edges.filter((edge) => edge.source !== action.payload && edge.target !== action.payload);
    },

    onRemoveEdge(state, action: PayloadAction<string>){
      state.edges = state.edges.filter((edge) => edge.id !== action.payload);
    },

    updateNodeData(state, action: PayloadAction<UpdateNodePayload>) {
      state.nodes = state.nodes.map((node) => {
        if (node.id === action.payload.nodeId) {
          node.data = { ...node.data, ...action.payload.newData };
        }
        return node;
      });
    },

    updateManyNodesData(state, action: PayloadAction<UpdateNodePayload[]>) {
      state.nodes = state.nodes.map((node) => {
        const nodeToUpdate = action.payload.find((update) => update.nodeId === node.id);
        if (nodeToUpdate) {
          return {
            ...node,
            data: { ...node.data, ...nodeToUpdate.newData },
          };
        }
        return node;
      });
    },

  },
});

export const {
  setNodes, setEdges, onNodesChange, setNode, onEdgesChange, onConnect, onRemoveNode, onRemoveEdge, updateNodeData, updateManyNodesData,
} = flowSlice.actions;

export default flowSlice.reducer;
